import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getRFIById, updateRFI, listRFIImages, uploadRFIImages, deleteRFIImage } from '../../../services/rfiService';
import { statusVariant } from '../../../utils/statusVariant';
import Badge from '../../../components/ui/Badge';
import useEscapeKey from '../../../hooks/useEscapeKey';
import { PROFESSIONS, RFI_STATUSES, statusLabel, formatDate, isAnswered } from './rfiConstants';

// Fiche d'un QRT, calquee sur AddExtraModal : ouverte en consultation (champs
// verrouilles), « Modifier » deverrouille sujet, profession, question et
// statut. Un QRT repondu reste modifiable (il n'est plus verrouille).
//
// La reponse ne se saisit JAMAIS ici : « Repondre » / « Modifier la reponse »
// ouvrent RFIQrtResponseModal (PDF obligatoire a la premiere reponse).

const TOP_FIELD = 'w-full h-[34px] px-3 py-2 text-xs leading-4 border border-gray-200 rounded-lg outline-none transition-colors placeholder:text-gray-400 placeholder:font-normal focus:border-primary focus:ring-2 focus:ring-primary/20';
const TOP_LABEL = 'block text-[11px] font-medium text-[#FF6B35] mb-1';
const QUESTION_MIN_H = 76;

// Zone de texte qui grandit avec son contenu (meme regle qu'AddExtraModal).
const autoGrow = (el, minHeight = 0) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.max(minHeight, el.scrollHeight + 2)}px`;
};

const RFIDetailModal = ({
  rfi,
  onClose,
  onSave,
  onStatusChange,
  onOpenResponse,
  onEditResponse,
  onDownloadPDF,
  projectId,
  escapeDisabled = false,
  isEditMode = false,
  startInView = false,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const [isViewing, setIsViewing] = useState(!!(startInView && isEditMode && rfi));
  const [form, setForm] = useState({
    subject: rfi?.subject || '',
    profession: rfi?.profession || '',
    question: rfi?.question || '',
    status: rfi?.status || 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [detailedRfi, setDetailedRfi] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [questionPhotos, setQuestionPhotos] = useState([]);
  // Photos ajoutees en edition : televersees a « Enregistrer ».
  const [newPhotos, setNewPhotos] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const photoInputRef = useRef(null);

  const answered = isAnswered(rfi?.status);

  useEscapeKey(onClose, !saving && !escapeDisabled);

  // Reponse complete (photos, URL signee du PDF) : seulement si le QRT est repondu.
  useEffect(() => {
    if (!answered || !rfi?.id) return undefined;
    let cancelled = false;
    setLoadingDetails(true);
    getRFIById(rfi?.id)?.then(({ data }) => {
      if (cancelled) return;
      if (data) setDetailedRfi(data);
      setLoadingDetails(false);
    });
    return () => { cancelled = true; };
  }, [rfi?.id, answered]);

  // Photos jointes a la question (seau rfi-documents).
  useEffect(() => {
    if (!projectId || !rfi?.id) return undefined;
    let cancelled = false;
    listRFIImages(projectId, rfi?.id)?.then(({ data }) => {
      if (!cancelled) setQuestionPhotos(data || []);
    });
    return () => { cancelled = true; };
  }, [projectId, rfi?.id]);

  // Liberation des apercus locaux a la fermeture de la fiche.
  const previewsRef = useRef([]);
  previewsRef.current = newPreviews;
  useEffect(() => () => previewsRef.current?.forEach((url) => URL.revokeObjectURL(url)), []);

  if (!rfi) return null;

  const displayRfi = detailedRfi || rfi;
  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const resetForm = () => setForm({
    subject: rfi?.subject || '',
    profession: rfi?.profession || '',
    question: rfi?.question || '',
    status: rfi?.status || 'draft',
  });

  const clearNewPhotos = () => {
    newPreviews?.forEach((url) => URL.revokeObjectURL(url));
    setNewPhotos([]);
    setNewPreviews([]);
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    const valid = files?.filter((f) => ['image/jpeg', 'image/png']?.includes(f?.type));
    setNewPhotos((prev) => [...prev, ...valid]);
    const previews = valid?.map((f) => URL.createObjectURL(f));
    setNewPreviews((prev) => [...prev, ...previews]);
    e.target.value = '';
  };

  const removeNewPhoto = (idx) => {
    URL.revokeObjectURL(newPreviews?.[idx]);
    setNewPhotos((prev) => prev?.filter((_, i) => i !== idx));
    setNewPreviews((prev) => prev?.filter((_, i) => i !== idx));
  };

  // Suppression immediate dans le Storage (pas differee a « Enregistrer »).
  const handleDeleteExistingPhoto = async (photo) => {
    if (!photo?.path) return;
    setDeletingPhoto(photo?.path);
    setError(null);
    const { error: delErr } = await deleteRFIImage(photo?.path);
    if (delErr) setError(t('rfi.detail.photoDeleteFailed'));
    else setQuestionPhotos((prev) => prev?.filter((p) => p?.path !== photo?.path));
    setDeletingPhoto(null);
  };

  const handleCancel = () => {
    clearNewPhotos();
    if (startInView) {
      resetForm();
      setError(null);
      setIsViewing(true);
    } else {
      onClose?.();
    }
  };

  const handleSave = async () => {
    const subject = form?.subject?.trim();
    if (!subject) { setError(t('rfi.create.subjectRequired')); return; }
    setSaving(true);
    setError(null);
    const { data, error: saveError } = await updateRFI(rfi?.id, {
      subject,
      profession: form?.profession || null,
      question: form?.question?.trim() || null,
    });
    if (saveError || !data) {
      setError(t('rfi.saveFailed'));
      setSaving(false);
      return;
    }
    // Statut : seulement les transitions hors « Repondu » (la reponse passe
    // par sa modale). Meme chemin que la pastille du tableau.
    let status = rfi?.status;
    if (form?.status !== rfi?.status && form?.status !== 'answered') {
      await onStatusChange?.(rfi?.id, form?.status);
      status = form?.status;
    }
    // Upload des nouvelles photos ajoutees en edition
    if (newPhotos?.length > 0 && projectId && rfi?.id) {
      await uploadRFIImages(projectId, rfi?.id, newPhotos);
      clearNewPhotos();
    }
    setSaving(false);
    onSave?.({ ...data, status });
  };

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    try { await onDownloadPDF?.(rfi); } finally { setGeneratingPDF(false); }
  };

  const handleDownloadResponsePdf = () => {
    const url = detailedRfi?.response_pdf_url;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a?.click();
  };

  // Selecteur de statut (modification) : sans « Repondu », qui passe par le
  // bouton dedie. Un QRT deja repondu garde son statut affiche, non choisissable.
  const statusOptions = RFI_STATUSES?.filter((s) => s?.value !== 'answered' || answered);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      // Clic hors de la fiche : ferme seulement en consultation, jamais en modification.
      onClick={(e) => { if (isViewing && !saving && e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1040px] flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <Icon name="MessageSquare" size={18} className="text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                {isViewing ? t('rfi.viewTitle') : t('rfi.editTitle')} · {rfi?.rfi_number}
              </h2>
              <p className="text-[11px] text-gray-500 truncate max-w-[640px]">{rfi?.subject || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{t('rfi.detail.statusLabel')} :</span>
              {isViewing ? (
                <Badge variant={statusVariant('rfi', rfi?.status)}>{statusLabel(t, rfi?.status)}</Badge>
              ) : (
                <select
                  value={form?.status}
                  onChange={(e) => handleChange('status', e?.target?.value)}
                  disabled={saving}
                  className="text-xs font-medium px-2 py-1 rounded-lg border border-gray-200 bg-white text-gray-700 outline-none cursor-pointer focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  {statusOptions?.map((s) => (
                    <option key={s?.value} value={s?.value} disabled={s?.value === 'answered'}>
                      {t(s?.labelKey) || s?.fallback}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
              title={t('common.close')}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              <Icon name="AlertCircle" size={14} />{error}
            </div>
          )}

          {/* En consultation, les champs sont verrouilles d'un bloc ; ils gardent leur apparence. */}
          <fieldset
            disabled={isViewing || saving}
            className="min-w-0 space-y-5 [&_:disabled]:cursor-default [&_input:disabled]:opacity-100 [&_input:disabled]:bg-white [&_input:disabled]:text-gray-900 [&_select:disabled]:opacity-100 [&_select:disabled]:bg-white [&_select:disabled]:text-gray-900 [&_textarea:disabled]:bg-white [&_textarea:disabled]:text-gray-900"
          >
            <div>
              <label className={TOP_LABEL}>
                {t('rfi.create.subjectLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.subject}
                onChange={(e) => handleChange('subject', e?.target?.value)}
                placeholder={t('rfi.create.subjectPlaceholder')}
                className={TOP_FIELD}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={TOP_LABEL}>{t('rfi.create.professionLabel')}</label>
                <select
                  value={form?.profession}
                  onChange={(e) => handleChange('profession', e?.target?.value)}
                  className={`${TOP_FIELD} bg-white text-gray-700`}
                >
                  <option value="">{t('rfi.create.professionPlaceholder')}</option>
                  {PROFESSIONS?.map((p) => <option key={p?.value} value={p?.value}>{t(`rfi.professions.${p?.key}`)}</option>)}
                </select>
              </div>
              <div>
                <label className={TOP_LABEL}>{t('rfi.detail.dateLabel')}</label>
                <p className="h-[34px] flex items-center text-xs text-gray-700">{formatDate(rfi?.created_at, language)}</p>
              </div>
            </div>

            <div>
              <label className={TOP_LABEL}>{t('rfi.create.questionLabel')}</label>
              <textarea
                ref={(el) => autoGrow(el, QUESTION_MIN_H)}
                value={form?.question}
                onChange={(e) => handleChange('question', e?.target?.value)}
                placeholder={t('rfi.create.questionPlaceholder')}
                className="w-full px-3 py-2.5 text-xs !leading-relaxed border border-gray-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-none overflow-hidden placeholder:text-gray-400 placeholder:font-normal"
                style={{ minHeight: `${QUESTION_MIN_H}px` }}
              />
            </div>
          </fieldset>

          {/* Reponse — juste sous la question, meme case et meme libelle.
              Lecture seule : la reponse se modifie par sa modale (🔒 workflow). */}
          {answered && (
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className={`${TOP_LABEL} !mb-0`}>
                  {t('rfi.response')}
                  {displayRfi?.answered_at && (
                    <span className="ml-2 font-normal text-gray-400">
                      {t('rfi.answeredAt')} {formatDate(displayRfi?.answered_at, language)}
                    </span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => onEditResponse?.(displayRfi)}
                  disabled={loadingDetails}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-orange-50 rounded-md transition-colors disabled:opacity-50"
                >
                  <Icon name="Pencil" size={12} />
                  {t('rfi.editResponse')}
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Icon name="Loader2" size={14} className="animate-spin" />
                  {t('common.loading')}
                </div>
              ) : (
                <>
                  <div
                    className="w-full px-3 py-2.5 text-xs !leading-relaxed text-gray-900 bg-white border border-gray-200 rounded-lg whitespace-pre-wrap break-words"
                    style={{ minHeight: `${QUESTION_MIN_H}px` }}
                  >
                    {displayRfi?.response_text || <span className="text-gray-400">—</span>}
                  </div>

                  {displayRfi?.response_pdf_url && (
                    <button
                      type="button"
                      onClick={handleDownloadResponsePdf}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors"
                    >
                      <Icon name="FileDown" size={13} />
                      {t('rfi.downloadResponsePdf')}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Photos de la question — toujours affichees : en edition, la zone
              d'ajout doit etre visible meme sans photo. */}
          <div>
            <p className={TOP_LABEL}>{t('rfi.create.photosLabel')}</p>

            {/* Photos existantes */}
            {questionPhotos?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {questionPhotos?.map((photo, idx) => (
                  <div key={photo?.path || idx} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                    <a
                      href={isViewing ? photo?.signedUrl : undefined}
                      target={isViewing ? '_blank' : undefined}
                      rel={isViewing ? 'noopener noreferrer' : undefined}
                      onClick={isViewing ? undefined : (e) => e?.preventDefault()}
                      className="block w-full h-full"
                    >
                      <img src={photo?.signedUrl} alt={t('rfi.create.photoPreviewAlt', { index: idx + 1 })} className="w-full h-full object-cover" />
                    </a>
                    {!isViewing && !saving && (
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingPhoto(photo)}
                        disabled={deletingPhoto === photo?.path}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity disabled:opacity-30"
                      >
                        {deletingPhoto === photo?.path
                          ? <Icon name="Loader2" size={14} className="text-white animate-spin" />
                          : <Icon name="X" size={14} className="text-white" />}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Nouvelles photos selectionnees (pas encore enregistrees) */}
            {!isViewing && newPreviews?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {newPreviews?.map((src, idx) => (
                  <div key={src} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-orange-300">
                    <img src={src} alt={t('rfi.create.photoPreviewAlt', { index: idx + 1 })} className="w-full h-full object-cover" />
                    {!saving && (
                      <button
                        type="button"
                        onClick={() => removeNewPhoto(idx)}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                      >
                        <Icon name="X" size={14} className="text-white" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Zone d'upload — en edition seulement */}
            {!isViewing && !saving && (
              <>
                <div
                  onClick={() => photoInputRef?.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors"
                >
                  <Icon name="ImagePlus" size={20} className="text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">{t('rfi.create.uploadHint')}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{t('rfi.create.uploadFormats')}</p>
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
              </>
            )}

            {/* Aucune photo et consultation */}
            {isViewing && questionPhotos?.length === 0 && (
              <p className="text-xs text-gray-400 italic">{t('rfi.detail.noPhotos')}</p>
            )}
          </div>

          {/* Photos de la reponse — a la fin, apres celles de la question */}
          {answered && !loadingDetails && displayRfi?.response_photos?.length > 0 && (
            <div>
              <p className={TOP_LABEL}>{t('rfi.detail.photosCount', { count: displayRfi?.response_photos?.length })}</p>
              <div className="flex flex-wrap gap-2">
                {displayRfi?.response_photos?.map((photo, idx) => (
                  <a
                    key={photo?.id || idx}
                    href={photo?.signed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-orange-400 transition-colors block"
                  >
                    <img src={photo?.signed_url} alt={t('rfi.detail.responsePhotoAlt', { index: idx + 1 })} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Pied */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={isViewing ? onClose : handleCancel}
            disabled={saving}
            className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isViewing ? t('common.close') : t('common.cancel')}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleGeneratePDF}
              disabled={generatingPDF || saving}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-60"
            >
              <Icon name={generatingPDF ? 'Loader2' : 'FileDown'} size={13} className={generatingPDF ? 'animate-spin' : ''} />
              {generatingPDF ? t('rfi.detail.generatingPdf') : t('rfi.detail.generatePdf')}
            </button>
            {!answered && (
              <button
                type="button"
                onClick={() => onOpenResponse?.(rfi)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-green-700 border border-green-200 bg-green-50 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-60"
              >
                <Icon name="Reply" size={13} />
                {t('rfi.actions.respond')}
              </button>
            )}
            {isViewing ? (
              <button
                type="button"
                onClick={() => setIsViewing(false)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
              >
                <Icon name="Pencil" size={13} />
                {t('common.edit')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
              >
                <Icon name={saving ? 'Loader2' : 'Check'} size={13} className={saving ? 'animate-spin' : ''} />
                {t('common.save')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFIDetailModal;
